import React, { useMemo, useState } from "react";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer } from "../hrForm";
import { cellInfo, daysFrom, isoWeekday, WEEKDAYS } from "../timeHelpers";
import { EmptyState, LoadState } from "../hrComponents";
import { cardStyle, formatDate, hhmm, todayYmd, useLoad } from "../hrUi";

const DAY_NAME = Object.fromEntries(WEEKDAYS);

// ROS-06/07: my published shifts for the next weeks, and a way to ask to swap with a colleague or change one day.
export default function MyRoster() {
  const from = todayYmd();
  const days = useMemo(() => daysFrom(from, 21), [from]);
  const { data, loading, error, reload } = useLoad(() => hrmsService.getMyRoster(days[0], days[days.length - 1]), []);
  const [asking, setAsking] = useState(null);
  const shifts = useMemo(() => data?.shifts || [], [data]);
  const byId = useMemo(() => new Map(shifts.map((s) => [s.id, s])), [shifts]);

  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {data ? (
        <div style={{ ...cardStyle, padding: "4px 16px" }}>
          {days.map((d) => {
            const cell = data.cells[d];
            const info = cellInfo(cell ? { ...cell, status: "PUBLISHED" } : undefined, byId);
            const s = cell && cell.shift_id ? byId.get(cell.shift_id) : null;
            return (
              <div key={d} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
                <div style={{ minWidth: 150 }}>
                  <strong>{DAY_NAME[isoWeekday(d)]}</strong> {formatDate(d)}
                </div>
                <div style={{ flex: 1 }}>
                  {info.kind === "SHIFT" ? (
                    <span>
                      <span style={{ display: "inline-block", width: 10, height: 10, background: info.color, borderRadius: 2, marginRight: 6 }} />
                      {s ? `${s.name} · ${hhmm(s.start_time)}-${hhmm(s.end_time)}` : "Shift"}
                    </span>
                  ) : info.kind === "EMPTY" ? (
                    <span style={{ color: "#9CA3AF" }}>Not published</span>
                  ) : (
                    <span style={{ color: "#6B7280" }}>{info.title}</span>
                  )}
                </div>
                {cell && cell.shift_id !== undefined && !cell.leave && d >= from ? (
                  <button type="button" className="btn btn-link btn-sm" onClick={() => setAsking(d)}>
                    Swap / change
                  </button>
                ) : null}
              </div>
            );
          })}
          {days.every((d) => !data.cells[d]) ? <EmptyState title="No published roster yet" text="Your roster appears here once HR publishes it." /> : null}
        </div>
      ) : null}
      <SwapDrawer date={asking} shifts={shifts} onHide={() => setAsking(null)} onDone={reload} />
    </LoadState>
  );
}

function SwapDrawer({ date, shifts, onHide, onDone }) {
  return (
    <FormDrawer
      show={!!date}
      title={date ? `Change ${formatDate(date)}` : "Change a day"}
      initial={{ swap_type: "CHANGE" }}
      fields={(v) => [
        { key: "swap_type", label: "What do you want", type: "select", required: true, options: [{ label: "Swap with a colleague", value: "SWAP" }, { label: "Work a different shift / have the day off", value: "CHANGE" }] },
        ...(v.swap_type === "SWAP"
          ? [{ key: "peer_id", label: "Colleague", type: "employee", required: true, hint: "They must agree first, then your manager decides." }]
          : [{ key: "to_shift_id", label: "Shift you want", type: "select", options: [{ label: "Day off", value: "OFF" }, ...shifts.map((s) => ({ label: `${s.code} · ${s.name}`, value: s.id }))] }]),
        { key: "reason", label: "Reason", type: "textarea", max: 500, rows: 2 },
      ]}
      onHide={onHide}
      onDone={onDone}
      onSave={(clean) => hrmsService.requestSwap({ ...clean, swap_date: date, to_shift_id: clean.to_shift_id === "OFF" ? null : clean.to_shift_id })}
      submitLabel="Send request"
    />
  );
}
